import React, { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, CalendarX2, Loader2 } from "lucide-react";
import { RatingBadge } from "./RatingBadge";
import { GuestSteppers } from "./GuestSteppers";
import { GuestDetailsForm } from "./GuestDetailsForm";
import { PriceBreakdown } from "./PriceBreakdown";
import { ContactRecap } from "./ContactRecap";
import { RulesAgreement } from "./RulesAgreement";
import { AuthPanel } from "@/components/auth/AuthPanel";
import { fmtAmount } from "@/lib/stayflowApi";
import { cancellationLines, fmtTime } from "@/lib/propertyRules";

// Full-screen mobile checkout wizard: Review trip → Sign in (mandatory) → Your details → Payment.
export const MobileCheckoutFlow = ({
  open, onClose,
  room, property,
  range, setRange, today, windowEnd, busyDates, nights,
  adults, setAdults, childrenCount, setChildrenCount, maxAdults = 1, maxChildren = 0, childRuleText = "", childrenAllowed = true,
  rules, quoteTerms, quoteLoading = false,
  quote, quoteError = "", total,
  form, onChange,
  gst, onGstChange, gstError,
  user, onSubmit,
}) => {
  const [step, setStep] = useState("review");
  const [editing, setEditing] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (open) { setStep("review"); setEditing(""); setShowDetails(false); setAgreed(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Signing in is never required — it's an optional detour from "Your details".
  const stepOrder = ["review", "details"];
  const stepIndex = Math.max(0, stepOrder.indexOf(step === "processing" || step === "auth" ? "details" : step));
  const totalSteps = stepOrder.length;

  const handleNext = () => {
    if (!range?.from || !range?.to || nights < 1) { toast.error("Please select your check-in and check-out dates."); return; }
    setStep("details");
  };

  const handleBack = () => {
    if (step === "auth") { setStep("details"); return; }
    const i = stepOrder.indexOf(step);
    if (i <= 0) onClose();
    else setStep(stepOrder[i - 1]);
  };

  const handleReserve = async () => {
    setStep("processing");
    setSubmitting(true);
    const ok = await onSubmit();
    setSubmitting(false);
    if (!ok) setStep("details");
  };

  const META = {
    review: { title: "Review your stay", subtitle: `Step 1 of ${totalSteps}` },
    auth: { title: "Sign in (optional)", subtitle: null },
    details: { title: "Your details", subtitle: `Step ${totalSteps} of ${totalSteps}` },
    processing: { title: "Confirming your booking", subtitle: null },
  };
  const meta = META[step];

  return (
    <div
      className="fixed inset-0 z-[70] bg-paper flex flex-col transition-transform duration-300 ease-out"
      style={{ transform: open ? "translateX(0)" : "translateX(100%)", pointerEvents: open ? "auto" : "none" }}
      data-testid="mobile-checkout-flow"
      aria-hidden={!open}
      inert={!open}
    >
      <header className="flex items-center gap-3 px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-3 border-b border-black/5 bg-white shrink-0">
        {step !== "processing" && (
          <button type="button" onClick={handleBack} aria-label="Back" data-testid="checkout-back-btn" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors shrink-0">
            <ArrowLeft className="w-5 h-5 text-ink" />
          </button>
        )}
        <div className="min-w-0">
          <p className="font-heading font-bold text-ink text-[15px] truncate">{meta.title}</p>
          {meta.subtitle && <p className="text-[11px] text-ink-faint">{meta.subtitle}</p>}
        </div>
      </header>

      {step !== "processing" && (
        <div className="h-1 bg-black/5 shrink-0">
          <div className="h-full bg-brand transition-all duration-300" style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }} />
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {step === "review" && (
          <div className="px-5 py-5 space-y-6" data-testid="checkout-review-body">
            <div className="flex items-center gap-3 rounded-2xl border border-black/8 bg-white p-3">
              {room?.cover_image && (
                <img src={room.cover_image} alt={room?.name} className="w-16 h-16 rounded-xl object-cover shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold text-ink truncate">{room?.name}</p>
                <p className="text-xs text-ink-soft truncate">{property?.name}{room?.type ? ` · ${room.type}` : ""}</p>
              </div>
              <RatingBadge source={room || {}} testid="checkout-rating-badge" className="shrink-0" />
            </div>

            <section>
              <h3 className="text-sm font-heading font-bold text-ink mb-2">Trip details</h3>
              <div className="rounded-2xl border border-black/8 divide-y divide-black/5 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold tracking-wider text-ink-faint uppercase">Dates</p>
                    <p className="text-sm font-medium text-ink mt-0.5 truncate" data-testid="checkout-dates-value">
                      {range?.from && range?.to ? `${format(range.from, "dd MMM")} – ${format(range.to, "dd MMM")} · ${nights} night${nights !== 1 ? "s" : ""}` : "Select dates"}
                    </p>
                  </div>
                  <button type="button" onClick={() => setEditing(editing === "dates" ? "" : "dates")} data-testid="checkout-change-dates" className="text-sm font-semibold text-brand hover:underline shrink-0 ml-3">
                    Change
                  </button>
                </div>
                {editing === "dates" && (
                  <div className="px-4 py-3">
                    <div className="rdp-wrap flex justify-center" style={{ "--rdp-cell-size": "40px" }}>
                      <DayPicker
                        mode="range" selected={range} onSelect={setRange}
                        defaultMonth={range?.from || today} numberOfMonths={1}
                        fromDate={today} toDate={windowEnd}
                        disabled={[{ before: today }, ...busyDates]}
                        data-testid="checkout-date-picker"
                      />
                    </div>
                    <button type="button" onClick={() => setEditing("")} className="mt-2 w-full text-center text-sm font-semibold text-brand" data-testid="checkout-dates-done">Done</button>
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-[10px] font-bold tracking-wider text-ink-faint uppercase">Guests</p>
                    <p className="text-sm font-medium text-ink mt-0.5" data-testid="checkout-guests-value">
                      {adults + childrenCount} guest{(adults + childrenCount) !== 1 ? "s" : ""}{childrenCount > 0 ? ` · ${childrenCount} child${childrenCount !== 1 ? "ren" : ""}` : ""}
                    </p>
                  </div>
                  <button type="button" onClick={() => setEditing(editing === "guests" ? "" : "guests")} data-testid="checkout-change-guests" className="text-sm font-semibold text-brand hover:underline shrink-0 ml-3">
                    Change
                  </button>
                </div>
                {editing === "guests" && (
                  <div className="px-4">
                    <GuestSteppers adults={adults} setAdults={setAdults} childrenCount={childrenCount} setChildrenCount={setChildrenCount} maxAdults={maxAdults} maxChildren={maxChildren} childRuleText={childRuleText} childrenAllowed={childrenAllowed} idPrefix="checkout-" />
                    <button type="button" onClick={() => setEditing("")} className="mb-3 w-full text-center text-sm font-semibold text-brand" data-testid="checkout-guests-done">Done</button>
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-black/8 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-ink-faint">Total amount</p>
                  <p className="font-heading font-extrabold text-2xl text-ink" data-testid="checkout-total-value">{total != null ? fmtAmount(total) : "Checking…"}</p>
                </div>
                <button type="button" onClick={() => setShowDetails((s) => !s)} data-testid="checkout-price-details-toggle" className="inline-flex items-center gap-1 text-sm font-semibold text-brand shrink-0">
                  Details <ChevronDown className={`w-4 h-4 transition-transform ${showDetails ? "rotate-180" : ""}`} />
                </button>
              </div>
              {showDetails && (
                <div className="mt-3 pt-3 border-t border-black/5">
                  <PriceBreakdown quote={quote} nights={nights} loading={quoteLoading} error={quoteError} idPrefix="checkout-" />
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-black/8 bg-white px-4 py-3 space-y-1" data-testid="checkout-stay-times">
              {rules?.check_in_from && <p className="text-xs text-ink-soft">Check-in from <span className="font-semibold text-ink">{fmtTime(rules.check_in_from)}</span>{rules.check_in_until ? ` until ${fmtTime(rules.check_in_until)}` : ""}</p>}
              {rules?.check_out_by && <p className="text-xs text-ink-soft">Check-out by <span className="font-semibold text-ink">{fmtTime(rules.check_out_by)}</span></p>}
            </section>

            <section className="flex items-start gap-2.5 rounded-2xl bg-brand-soft/40 border border-brand/15 px-4 py-3">
              <CalendarX2 className="w-4 h-4 text-brand mt-0.5 shrink-0" />
              <div className="text-xs text-ink-soft space-y-0.5" data-testid="checkout-cancellation-policy">
                {quoteLoading && cancellationLines(quoteTerms).length === 0
                  ? <p>Checking cancellation terms…</p>
                  : cancellationLines(quoteTerms).map((l) => <p key={l}>{l}</p>)}
              </div>
            </section>
          </div>
        )}

        {step === "auth" && (
          <div className="px-5 py-6" data-testid="checkout-auth-body">
            <p className="text-sm text-ink-soft mb-5">Signing in is optional — it autofills your details and keeps this booking in your account.</p>
            <AuthPanel compact recaptchaId="sf-checkout-recaptcha" onSuccess={() => setStep("details")} />
            <button type="button" onClick={() => setStep("details")} data-testid="checkout-skip-auth" className="mt-5 w-full text-center text-sm font-semibold text-ink underline">
              Continue as guest
            </button>
          </div>
        )}

        {step === "details" && (
          <div className="px-5 py-6 space-y-5" data-testid="checkout-details-body">
            <p className="text-sm text-ink-soft">Double-check your details — we'll use these to confirm your reservation.</p>
            {!user && (
              <button type="button" onClick={() => setStep("auth")} data-testid="checkout-signin-optional" className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-black/15 bg-white h-11 text-sm font-semibold text-ink">
                Sign in (optional) — faster next time
              </button>
            )}
            <GuestDetailsForm form={form} onChange={onChange} idPrefix="checkout-" gst={gst} onGstChange={onGstChange} gstError={gstError} />
            <ContactRecap phone={form.guest_phone} email={form.guest_email} testid="checkout-contact-recap" />
            <RulesAgreement
              quoteTerms={quoteTerms} rules={rules} loading={quoteLoading}
              agreed={agreed} setAgreed={setAgreed} idPrefix="checkout-"
            />
          </div>
        )}

        {step === "processing" && (
          <div className="flex-1 h-full flex flex-col items-center justify-center gap-3 px-8 text-center">
            <Loader2 className="w-8 h-8 text-brand animate-spin" />
            <p className="text-sm text-ink-soft">Setting up secure payment…</p>
          </div>
        )}
      </div>

      {step === "review" && (
        <div className="border-t border-black/10 bg-white px-5 pt-3 pb-[calc(0.9rem+env(safe-area-inset-bottom))] shrink-0">
          <button type="button" onClick={handleNext} data-testid="checkout-next-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold h-12 text-[15px] transition-transform hover:-translate-y-0.5">
            Next
          </button>
          <p className="mt-2 text-center text-[11px] text-ink-faint">You won't be charged yet — pay securely on the next step.</p>
        </div>
      )}

      {step === "details" && (
        <div className="border-t border-black/10 bg-white px-5 pt-3 pb-[calc(0.9rem+env(safe-area-inset-bottom))] shrink-0">
          <button type="button" onClick={handleReserve} disabled={submitting || !agreed} data-testid="checkout-reserve-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold h-12 text-[15px] transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : "Reserve & pay"}
          </button>
          {!agreed && <p className="mt-2 text-center text-[11px] text-ink-faint">Tick the agreement above to continue</p>}
        </div>
      )}
    </div>
  );
};

export default MobileCheckoutFlow;
